import React, { useCallback, useState, useEffect, useMemo } from 'react'
import classNames from 'classnames'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { DropdownMenuGroup, MenuChild } from 'pharmacy/src/input/dropdownMenu'
import { Icon } from 'pharmacy/src/display/icon'

import { withPermissions, withAnyPermissions } from 'mednet-util/src/permission'
import { authItems } from 'mednet-util/src/constants/permission'
import { CME_PAGE_VISIT_SOURCE } from 'mednet-util/src/constants/cme'
import { mixpanel } from 'mednet-util/src/tracking'

import {
  useCmeAndMocAvailabilityCheck,
  useTrackCmePageVisit,
} from 'mednet-cns/src/hooks/cme'
import { useOpenAnswerRequestsCount } from 'mednet-cns/src/hooks/user'

import { UserImage } from 'pharmacy/src/user/userImage'

import { EXTERNAL_LINKS } from 'constants/links'
import { ROUTES } from 'constants/routes'
import { PAGES } from 'constants/pages'

import { getAboutNavigationLinksHierarchy } from 'anterior-util/aboutNavigationLinksHierarchy'

import HeaderLink from './headerLink'

import css from './headerDropdown.scss'
import useNewSearchExperienceFlag from './useNewSearchExperienceFlag'

const EditProfilePage = withPermissions(authItems.editProfile)(() => (
  <MenuChild>{EXTERNAL_LINKS.editProfile}</MenuChild>
))

const MailboxPage = withPermissions(authItems.manageMailbox)(() => (
  <MenuChild>{EXTERNAL_LINKS.mailbox}</MenuChild>
))

const ModeratorPage = withPermissions(authItems.moderator)(() => (
  <MenuChild>{EXTERNAL_LINKS.moderatorPage}</MenuChild>
))

const AssociateLeaderboardPage = withPermissions(
  authItems.viewAssociateLeaderboard
)(() => <MenuChild>{EXTERNAL_LINKS.associateLeaderboard}</MenuChild>)

const AdminPage = withPermissions(authItems.admin)(() => (
  <MenuChild>{EXTERNAL_LINKS.admin}</MenuChild>
))

// V2 Admin Dashboard - visible to admin OR super_moderator OR moderator
const V2AdminDashboardPage = withAnyPermissions([
  authItems.admin,
  authItems.superModerator,
  authItems.moderator,
])(() => <MenuChild>{EXTERNAL_LINKS.v2AdminDashboard}</MenuChild>)

const CmeLink = ({ location }) => {
  const { isCmeOrMocAvailableForUser, isMocEnabledForUser } =
    useCmeAndMocAvailabilityCheck()
  const trackCmePageVisit = useTrackCmePageVisit()

  const handleClick = useCallback(() => {
    if (location.pathname === '/cme') {
      return
    }

    trackCmePageVisit(CME_PAGE_VISIT_SOURCE.SOURCE_MENU)
    mixpanel.track('viewed_redeem_cme_form_page', {
      source: CME_PAGE_VISIT_SOURCE.SOURCE_MENU,
    })
  }, [location.pathname, trackCmePageVisit])

  if (!isCmeOrMocAvailableForUser) {
    return null
  }

  return (
    <MenuChild>
      {{
        label: isMocEnabledForUser ? 'Redeem CME & MOC' : 'Redeem CME',
        pathname: ROUTES[PAGES.CME_DASHBOARD].path,
        external: false,
        onClick: handleClick,
      }}
    </MenuChild>
  )
}

const DropDownIconElement = ({ rotated }) => {
  return (
    <Icon
      className={classNames(css.dropDownIcon, {
        [css.dropDownRotatedIcon]: rotated,
      })}
      icon={['fas', 'chevron-down']}
    />
  )
}

const HeaderDropdown = (props) => {
  const { className, isGuest, userData, history } = props
  const flags = useFlags()
  const { enableExpertDash, enableMednetGpt } = flags
  const { enableNewSearchExperience } = useNewSearchExperienceFlag()
  const [openAnswerRequestsCount] = useOpenAnswerRequestsCount()

  // Add click handler for Sign In
  const handleSignInClick = useCallback((e) => {
    e.preventDefault()

    // Preserve existing query params
    const params = new URLSearchParams(window.location.search)
    const queryString = params.toString()

    // Always go to /user/login - PHP will redirect to V2 if needed
    window.location.href = queryString
      ? `${ROUTES[PAGES.LOGIN].path}?${queryString}`
      : ROUTES[PAGES.LOGIN].path
  }, [])

  const [currentLocation, setCurrentLocation] = useState({
    pathname: history.location.pathname,
    hash: history.location.hash,
  })

  const getActiveClassName = useCallback(
    (targetPathname, targetHash) => {
      return currentLocation.pathname === targetPathname &&
        (((!currentLocation.hash || currentLocation.hash === '#') &&
          !targetHash) ||
          currentLocation.hash === `#${targetHash}`)
        ? css.activePage
        : undefined
    },
    [currentLocation]
  )

  const getInternalPageLink = useCallback(
    (page) => {
      return {
        label: page,
        pathname: ROUTES[page].path,
        external: false,
        labelClassName: getActiveClassName(ROUTES[page].path),
      }
    },
    [getActiveClassName]
  )

  useEffect(() => {
    if (props.history) {
      const unlistenToLocation = props.history.listen(() => {
        setCurrentLocation({
          pathname: history.location.pathname,
          hash: history.location.hash,
        })
      })

      return unlistenToLocation
    }

    return () => {}
  }, [])

  const aboutUsMenu = useMemo(
    () => getAboutNavigationLinksHierarchy(getInternalPageLink, isGuest),
    [isGuest, getInternalPageLink]
  )

  const headerMenu = useMemo(() => {
    let menu = undefined

    if (isGuest) {
      const communityMenu = aboutUsMenu.find(
        (item) => item.header === 'Community'
      )

      if (communityMenu) {
        communityMenu.menuClassName = css.wideMenu
      }

      menu = [
        ...aboutUsMenu,
        {
          header: 'Contact Us',
          pathname: ROUTES[PAGES.CONTACT_US].path,
          external: false,
          className: getActiveClassName(ROUTES[PAGES.CONTACT_US].path),
        },
        {
          header: 'Sign In',
          onClick: handleSignInClick,
          className: css.signIn,
        },
      ]
    } else {
      const badgeText = openAnswerRequestsCount
        ? openAnswerRequestsCount > 9
          ? '9+'
          : openAnswerRequestsCount
        : 0
      const badgeComponent = badgeText ? (
        <div className={css.badge}>
          <span className={css.badgeText}>{badgeText}</span>
        </div>
      ) : null

      menu = [
        ...(enableExpertDash
          ? [
              {
                header: (
                  <div style={{ position: 'relative' }}>
                    {badgeComponent}
                    Expert
                    <span className={css.expertDashboardExtendedDashText}>
                      &nbsp;Dash
                    </span>
                    <span className={css.expertDashboardExtendedBoardText}>
                      board
                    </span>
                  </div>
                ),
                pathname: '/v2/expert',
                external: true,
              },
            ]
          : []),
        ...(enableMednetGpt
          ? [
              {
                header: (
                  <div>
                    Mednet AI{' '}
                    <span className={css.mednetGptBetaBadge}>BETA</span>
                  </div>
                ),
                pathname: '/v2/mednetGpt',
                external: true,
              },
            ]
          : []),
        ...(!enableNewSearchExperience
          ? [
              {
                header: 'Topics',
                pathname: '/topic/index',
                external: true,
              },
            ]
          : [
              {
                header: (
                  <div className={css.askQuestionButton} href="/question/ask">
                    <Icon icon={['fal', 'plus']} />
                    Ask
                    <span className={css.askQuestionButtonExtendedText}>
                      {' '}
                      an Expert
                    </span>
                  </div>
                ),
                pathname: '/question/ask',
                external: true,
                className: css.askQuestionButtonContainer,
              },
            ]),
        {
          header: enableNewSearchExperience ? (
            <UserImage
              className={css.userImage}
              userId={userData?.userId}
              imageVersion={userData?.imageVersion}
            />
          ) : (
            'My Account'
          ),
          className: enableNewSearchExperience ? css.userImageContainer : '',
          menuClassName: css.accountMenu,
          children: [
            <MailboxPage key="mailboxPage" />,
            <EditProfilePage key="editProfilePage" />,
            <CmeLink key="cmePage" location={history.location} />,
            EXTERNAL_LINKS.customizeFeed,
            <ModeratorPage key="moderatorPage" />,
            <AssociateLeaderboardPage key="associateLeaderboardPage" />,
            <AdminPage key="adminPage" />,
            <V2AdminDashboardPage key="v2AdminDashboardPage" />,
            <hr key="hr3" />,
            ...aboutUsMenu,
            <hr key="hr3" />,
            getInternalPageLink(PAGES.CONTACT_US),
            <hr key="hr2" />,
            EXTERNAL_LINKS.logout,
          ],
        },
      ]
    }

    return menu
  }, [
    isGuest,
    getInternalPageLink,
    getActiveClassName,
    enableExpertDash,
    openAnswerRequestsCount,
  ])

  return (
    <div
      className={
        enableNewSearchExperience ? css.newSearchHeaderDropdownContainer : ''
      }
    >
      <DropdownMenuGroup
        HeaderComponent={HeaderLink} // depending on isGeust pass headerComponentProps
        className={className}
        menu={headerMenu}
        isNarrow
        history={history}
        fullWidthItemsInSmallScreen={isGuest}
        displayIcon={isGuest}
        IconElement={DropDownIconElement}
        popupHorizontalPosition="right"
        addSeparatorForSubmenu={false}
      />
    </div>
  )
}

export default HeaderDropdown
